import { FC, useState, useEffect } from 'react'
import '@ant-design/compatible/assets/index.css'
import { Input, Form } from 'antd'
import { WarningModal } from '@User/MyAccountPage/components'
import PasswordStrength from '@User/SignUpPage/components/PasswordStrength'
import pageStyles from '@User/MyAccountPage/styles.css'

import Box from '~/core/Box'
import Button from '~/core/Button'
import { IdentityType } from '~/redux-modules/identity/type'
import { NO_FEEDBACK_PASSWORD_RULE } from '~/apps/consumer/pages/User/ResetPasswordPage/constants'
import { handleUpdatePassword } from '../logic'
import {
    PASSWORD,
    NEW_PASSWORD,
    CONFIRM_PASSWORD,
    PASSWORD_PLACEMENT,
    PASSWORD_RULE_REQUIRED,
    REQUIRED_FIELDS,
} from '../constants'
import { useDisabledFormSubmitButton } from '~/core/hooks/useDisabledFormSubmitButton'
import { useUpdatePasswordUserProfileMutation } from '~/gql/graphql'

import { generatePasswordLengthRule } from './logic'

interface PasswordFormPropsType {
    identity: IdentityType
    warning: boolean
    onCancel: () => void
    setCurrentForm: Function
}

const MIN_PASSWORD_LENGTH = 12
const MAX_PASSWORD_LENGTH = 64

export const PASSWORD_RULES = [
    PASSWORD_RULE_REQUIRED,
    ...generatePasswordLengthRule(0, MAX_PASSWORD_LENGTH),
]
const PASSWORD_RULES_NEW = [
    PASSWORD_RULE_REQUIRED,
    ...generatePasswordLengthRule(MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH),
]

const PasswordForm: FC<PasswordFormPropsType> = ({
    identity,
    onCancel,
    warning,
    setCurrentForm,
}) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [updatePassword] = useUpdatePasswordUserProfileMutation()
    const [form] = Form.useForm()

    useEffect(() => {
        setCurrentForm(form)
        // only run once on first render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        onFieldsChange,
        onValuesChange,
        disableFormSubmitButton,
        formValues,
    } = useDisabledFormSubmitButton(REQUIRED_FIELDS)

    const handlePasswordFormSubmit = () =>
        handleUpdatePassword({
            form,
            setLoading,
            identity,
            onCancel,
            updatePassword,
        })

    const compareToFirstPassword = (
        _: any,
        value: string,
        callback: Function,
    ) => {
        if (value && value !== form.getFieldValue(NEW_PASSWORD)) {
            callback('The two inputs you have entered do not match')
        } else {
            callback()
        }
    }

    const CONFIRM_PASSWORD_RULE = [
        ...PASSWORD_RULES_NEW,
        { validator: compareToFirstPassword },
    ]

    return (
        <Box flex="1" className={pageStyles.mobileFormBox}>
            {warning && (
                <WarningModal
                    visible={warning}
                    onClose={onCancel}
                    loading={loading}
                    onConfirm={handlePasswordFormSubmit}
                />
            )}
            <Box direction="col" className={pageStyles.fixedForm}>
                <Form
                    layout="vertical"
                    form={form}
                    onValuesChange={onValuesChange}
                    onFieldsChange={onFieldsChange}
                >
                    <Form.Item
                        label="Enter current password"
                        required
                        style={{ marginBottom: '4px' }}
                        hasFeedback
                        rules={PASSWORD_RULES}
                        name={PASSWORD}
                    >
                        <Input.Password data-test-target={PASSWORD} />
                    </Form.Item>
                    <Form.Item
                        label="Create new password"
                        required
                        style={{ marginBottom: '4px' }}
                        hasFeedback
                        help={
                            <PasswordStrength
                                value={formValues[NEW_PASSWORD]}
                            />
                        }
                        rules={NO_FEEDBACK_PASSWORD_RULE}
                        name={NEW_PASSWORD}
                    >
                        <Input.Password
                            placeholder={`Must contain at least ${MIN_PASSWORD_LENGTH} characters`}
                            data-test-target={NEW_PASSWORD}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Confirm new password"
                        required
                        style={{ marginBottom: '4px' }}
                        hasFeedback
                        rules={CONFIRM_PASSWORD_RULE}
                        name={CONFIRM_PASSWORD}
                    >
                        <Input.Password data-test-target={CONFIRM_PASSWORD} />
                    </Form.Item>
                </Form>
            </Box>
            <Box className={pageStyles.mobileButtonBox}>
                <Button
                    name="save"
                    className={pageStyles.mobileRightButton}
                    placement={PASSWORD_PLACEMENT}
                    data-test-target={`${PASSWORD_PLACEMENT}-save`}
                    type="primary"
                    onClick={handlePasswordFormSubmit}
                    loading={loading}
                    disabled={loading || disableFormSubmitButton}
                >
                    Save
                </Button>
                <Button
                    name="cancel"
                    placement={PASSWORD_PLACEMENT}
                    data-test-target={`${PASSWORD_PLACEMENT}-cancel`}
                    onClick={onCancel}
                    className={pageStyles.mobileLeftButton}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    )
}

export default PasswordForm
